import { AuditAction, AuditChannel, AuditPrincipalType } from '../../api';

/**
 * The words the audit viewer uses, and the one list it is not allowed to write
 * by hand.
 *
 * A lookup module like [`mailbox`](./mailbox.ts) and
 * [`reviewQueue`](./reviewQueue.ts), and here for the same reason: an action
 * appears in the filter, in the table and in the expanded entry, and
 * "Movimentação aprovada" spelled two ways across them is how a screen stops
 * being trusted.
 *
 * {@link ACTION_LABELS} is typed `Record<AuditAction, string>` on purpose. The
 * contract owns that enumeration -- it is `AuditAction` in the generated types,
 * regenerated from `openapi.json` -- so the day the backend records a new kind
 * of event this file stops compiling until somebody says what it is called in
 * pt-BR. And {@link ACTION_OPTIONS} is built from the generated object rather
 * than from this record, so the filter offers exactly what the API accepts even
 * in the moment before the label exists.
 */

/** What happened, in the words an analyst would use. */
export const ACTION_LABELS: Record<AuditAction, string> = {
  [AuditAction.CREATE]: 'Registro criado',
  [AuditAction.UPDATE]: 'Registro alterado',
  [AuditAction.DELETE]: 'Registro removido',
  [AuditAction.COMPANY_CNPJ_SET]: 'CNPJ da empresa definido ou corrigido',
  [AuditAction.COMPANY_GROUP_CREATED]: 'Grupo econômico cadastrado',
  [AuditAction.COMPANY_GROUP_RENAMED]: 'Grupo econômico renomeado',
  [AuditAction.COMPANY_GROUP_ASSIGNED]: 'Empresa colocada em um grupo econômico',
  [AuditAction.COMPANY_GROUP_MOVED]: 'Empresa movida para outro grupo econômico',
  [AuditAction.COMPANY_GROUP_REMOVED]: 'Empresa retirada do grupo econômico',
  [AuditAction.COMPANY_FILE_NAME_LABEL_SET]: 'Nome nos arquivos da seguradora alterado (empresa)',
  [AuditAction.COMPANY_GROUP_FILE_NAME_LABEL_SET]:
    'Nome nos arquivos da seguradora alterado (grupo econômico)',
  [AuditAction.COMPANY_TERMINATION_DOCUMENT_POLICY_SET]:
    'Política de documento de rescisão alterada',
  [AuditAction.SIGN_IN_SUCCEEDED]: 'Entrada no sistema',
  [AuditAction.SIGN_IN_FAILED]: 'Tentativa de entrada recusada',
  [AuditAction.MACHINE_CLIENT_AUTHENTICATED]: 'Integração autenticada',
  [AuditAction.CREDENTIAL_ISSUED]: 'Credencial emitida',
  [AuditAction.CREDENTIAL_ROTATED]: 'Credencial renovada',
  [AuditAction.CREDENTIAL_REVOKED]: 'Credencial revogada',
  [AuditAction.REFRESH_TOKEN_ISSUED]: 'Sessão do navegador emitida',
  [AuditAction.REFRESH_TOKEN_REUSED]: 'Sessão revogada por reuso de credencial',
  [AuditAction.SIGN_OUT_SUCCEEDED]: 'Saída do sistema',
  [AuditAction.ROSTER_DELTA_SUBMITTED]: 'Movimentação enviada',
  [AuditAction.ROSTER_DELTA_APPROVED]: 'Movimentação aprovada',
  [AuditAction.ROSTER_DELTA_REJECTED]: 'Movimentação recusada',
  [AuditAction.ROSTER_DELTA_CANCELLED]: 'Movimentação cancelada',
  [AuditAction.ROSTER_DELTA_RETURNED]: 'Movimentação devolvida',
  [AuditAction.ROSTER_DELTA_DATE_CORRECTED]: 'Data da movimentação corrigida',
  [AuditAction.SNAPSHOT_GENERATED]: 'Arquivo do mês gerado',
  [AuditAction.SNAPSHOT_APPROVED]: 'Arquivo do mês aprovado',
  [AuditAction.REVIEW_CYCLE_APPROVED]: 'Ciclo de análise aprovado',
  [AuditAction.REVIEW_CYCLE_REOPENED]: 'Ciclo de análise reaberto',
  [AuditAction.SUBMISSION_PREPARED]: 'Envio preparado',
  [AuditAction.SUBMISSION_SENT]: 'Envio realizado',
  [AuditAction.SUBMISSION_FAILED]: 'Envio recusado',
  [AuditAction.CONSOLIDATED_SUBMISSION_PREPARED]: 'Arquivo único preparado',
  [AuditAction.CONSOLIDATED_SUBMISSION_SENT]: 'Arquivo único enviado',
  [AuditAction.CONSOLIDATED_SUBMISSION_FAILED]: 'Arquivo único recusado',
  [AuditAction.GROUP_SUBMISSION_PREPARED]: 'Arquivo único do grupo preparado',
  [AuditAction.GROUP_SUBMISSION_SENT]: 'Arquivo único do grupo enviado',
  [AuditAction.GROUP_SUBMISSION_FAILED]: 'Arquivo único do grupo recusado',
  [AuditAction.OFF_CYCLE_NOTICE_RAISED]: 'Aviso fora do ciclo gerado',
  [AuditAction.OFF_CYCLE_NOTICE_SENT]: 'Aviso fora do ciclo comunicado',
  [AuditAction.OFF_CYCLE_NOTICE_FAILED]: 'Aviso fora do ciclo recusado',
  [AuditAction.SPREADSHEET_UPLOAD_SUBMITTED]: 'Planilha enviada',
  [AuditAction.SPREADSHEET_UPLOAD_COMMITTED]: 'Planilha confirmada',
  [AuditAction.SPREADSHEET_UPLOAD_ABSENCES_READ]: 'Seguradas fora da planilha consultadas',
  [AuditAction.GROUP_SPREADSHEET_UPLOAD_SUBMITTED]: 'Planilha do grupo enviada',
  [AuditAction.GROUP_SPREADSHEET_UPLOAD_COMMITTED]: 'Planilha do grupo confirmada',
  [AuditAction.MAIL_MESSAGE_INGESTED]: 'Mensagem processada',
  [AuditAction.MAIL_MESSAGE_REFUSED]: 'Mensagem sem nada aproveitável',
  [AuditAction.MAIL_MESSAGE_DISCARDED]: 'Mensagem descartada',
  [AuditAction.MAILBOX_SENDER_REGISTERED]: 'Remetente autorizado',
  [AuditAction.MAILBOX_SENDER_REVOKED]: 'Autorização de remetente retirada',
  [AuditAction.DOCUMENT_UPLOADED]: 'Documento enviado',
  [AuditAction.DOCUMENT_LINKED]: 'Documento vinculado',
  [AuditAction.DOCUMENT_UNLINKED]: 'Documento desvinculado',
  [AuditAction.DOCUMENT_DOWNLOADED]: 'Documento baixado',
  [AuditAction.DOCUMENT_SUPERSEDED]: 'Documento substituído',
  [AuditAction.DOCUMENT_ACCESS_REFUSED]: 'Acesso a documento recusado',
  [AuditAction.CERTIFICATE_UPLOADED]: 'Certificado A1 instalado',
  [AuditAction.CERTIFICATE_SUPERSEDED]: 'Certificado A1 substituído',
  [AuditAction.CERTIFICATE_DEACTIVATED]: 'Certificado A1 desativado',
  [AuditAction.ESOCIAL_DIAGNOSTIC_RAN]: 'Conexão com o eSocial testada',
  [AuditAction.ESOCIAL_EVENTS_FETCHED]: 'Eventos do eSocial buscados',
  [AuditAction.ESOCIAL_EVENTS_COMMITTED]: 'Eventos do eSocial confirmados',
  [AuditAction.ESOCIAL_PROBE_RAN]: 'Sondagem de capacidades do eSocial executada',
  [AuditAction.ESOCIAL_PROBE_EVIDENCE_READ]: 'Evidência da sondagem do eSocial aberta',
  [AuditAction.ESOCIAL_WORKER_LIST_CAPTURED]: 'Lista de trabalhadores do fechamento capturada',
  [AuditAction.ESOCIAL_PORTAL_WALK_RAN]: 'Percurso no portal do eSocial executado',
  [AuditAction.ESOCIAL_PORTAL_WALK_EVIDENCE_READ]:
    'Evidência do percurso no portal do eSocial aberta',
  [AuditAction.ESOCIAL_PORTAL_WALK_BUNDLE_READ]: 'Pacote do percurso no portal do eSocial baixado',
  [AuditAction.ESOCIAL_PORTAL_SESSION_RAN]:
    'Sessão no portal do eSocial executada (um login, várias empresas)',
  [AuditAction.ESOCIAL_WORKER_REPORT_REQUESTED]: 'Relação de trabalhadores solicitada ao eSocial',
  [AuditAction.ESOCIAL_WORKER_REPORT_COLLECTED]: 'Relação de trabalhadores do eSocial coletada',
  [AuditAction.ESOCIAL_WORKER_REPORT_FILE_READ]: 'Arquivo da relação de trabalhadores aberto',
  [AuditAction.NOTIFICATION_DELIVERED]: 'Aviso entregue ao cliente',
  [AuditAction.CLOCK_SIMULATION_SET]: 'Data e hora do ambiente simuladas',
  [AuditAction.CLOCK_SIMULATION_CLEARED]: 'Data e hora do ambiente restauradas',
};

/**
 * Every action the contract defines, in the order it defines them.
 *
 * Read off the generated `AuditAction` object, never off a list kept here: a
 * filter that offers an action the API does not know returns nothing and looks
 * broken, and one that omits an action the API does know hides events from the
 * person whose job is to find them.
 */
export const ACTION_OPTIONS: readonly { readonly value: AuditAction; readonly label: string }[] =
  Object.values(AuditAction).map((action) => ({
    value: action,
    label: ACTION_LABELS[action],
  }));

/** How the change reached us. */
export const CHANNEL_LABELS: Record<AuditChannel, string> = {
  [AuditChannel.API]: 'API',
  [AuditChannel.CLI]: 'Linha de comando',
  [AuditChannel.SCHEDULED]: 'Rotina agendada',
  [AuditChannel.BACKGROUND]: 'Processo interno',
};

/** What kind of principal acted. */
export const PRINCIPAL_LABELS: Record<AuditPrincipalType, string> = {
  [AuditPrincipalType.VYTTECH_STAFF]: 'Equipe vyttech',
  [AuditPrincipalType.CUSTOMER_USER]: 'Usuário do cliente',
  [AuditPrincipalType.MACHINE_CLIENT]: 'Integração',
  [AuditPrincipalType.SYSTEM]: 'Sistema',
};

export const AUDIT_COPY = {
  title: 'Auditoria',
  description:
    'Quem mudou o quê, quando e por qual canal. É o registro que o sistema escreve sozinho: ' +
    'esta tela só lê, e não existe forma de escrever nem de apagar uma entrada.',
  filtersTitle: 'Filtros',
  filtersDescription:
    'Combináveis: cada campo preenchido estreita o resultado. Em branco, o campo não filtra nada.',
  apply: 'Aplicar filtros',
  clear: 'Limpar filtros',
  action: 'Ação',
  anyAction: 'Qualquer ação',
  company: 'Empresa',
  anyCompany: 'Todas as empresas',
  companyHint:
    'Sem empresa escolhida, a trilha vem de todas elas — inclusive do que não pertence a ' +
    'nenhuma, como uma conta da equipe vyttech.',
  entityType: 'Tipo de registro',
  entityTypeHint: 'Como o domínio o chama, por exemplo insured_person.',
  entityId: 'Registro',
  entityIdHint: 'O identificador do registro afetado. Traz a história inteira dele.',
  actorId: 'Quem fez',
  actorIdHint: 'O identificador do usuário, da integração ou do sistema responsável.',
  correlationId: 'Requisição',
  correlationIdHint:
    'O identificador que todas as entradas de uma mesma requisição — ou de uma mesma rotina — ' +
    'compartilham.',
  occurredFrom: 'A partir de',
  occurredFromHint: 'Inclusivo: entradas deste dia entram. Em UTC, como a API conta.',
  occurredTo: 'Até antes de',
  occurredToHint:
    'Exclusivo: entradas deste dia ficam de fora. Em UTC. Assim dois períodos vizinhos não se ' +
    'sobrepõem nem deixam buraco.',
  entriesTitle: 'Entradas',
  entriesDescription: 'Mais recentes primeiro.',
  noEntries: 'Nenhuma entrada',
  noEntriesHint: 'Nada foi registrado com esses filtros. Tente afrouxar um deles.',
  loading: 'Carregando a trilha…',
  loadError: 'Não foi possível carregar a trilha de auditoria',
  expand: 'Ver detalhes',
  collapse: 'Ocultar detalhes',
  detailTitle: 'O que mudou',
  clientAddress: 'Endereço de origem',
  noClientAddress: 'Sem requisição: nada veio de um endereço.',
  filterByCorrelation: 'Filtrar por esta requisição',
  correlationHelp: 'Clique no identificador para ver tudo o que esta requisição fez.',
  noChanges: 'Esta entrada não registra mudança de campo: é um acontecimento, não uma edição.',
  before: 'Antes',
  after: 'Depois',
  /** `before: null` is the contract's way of saying the record was not there yet. */
  didNotExist: 'não existia',
  /** `after: null` is the contract's way of saying the record stopped being there. */
  ceasedToExist: 'deixou de existir',
  outsideCompany: 'Fora de uma empresa',
  previous: 'Página anterior',
  next: 'Próxima página',
  /**
   * Said on the screen because it is the thing a person has to know before
   * reading a salary off it. The API decrypts these values only to answer this
   * request, and whoever opened the entry is in the trail too.
   */
  personalData:
    'Os valores abertos aqui são dados pessoais — salários, documentos, datas de nascimento. ' +
    'Abrir uma entrada é uma leitura registrada como qualquer outra.',
} as const;

/**
 * A day chosen in a date field, as the instant the API filters by.
 *
 * The field gives `yyyy-mm-dd` and the endpoint takes a timestamp in UTC, so
 * the day is anchored at midnight UTC and said so in the field's hint. Guessing
 * the reader's timezone instead would make "a partir de 01/04" mean a different
 * moment for a reader in Lisbon than for one in Manaus, on a screen whose
 * entire job is to say exactly when something happened.
 */
export function asInstant(day: string): string {
  return day === '' ? '' : `${day}T00:00:00Z`;
}

/**
 * One recorded value, shown as it was recorded.
 *
 * Nothing here reformats: an audit trail that prettifies what it stored is no
 * longer a copy of what it stored, and "the salary was 7.500,00" is a different
 * claim from "the salary was `7500.00`". `null` is the one value with a meaning
 * of its own, and it is given words by the caller rather than here, because
 * which words depend on whether it is the before or the after.
 */
export function formatRecordedValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value) ?? '';
}
